// ─── CHAD V3 Chat Service ────────────────────────────────────────────────────
// Handles sending questions to /api/chat and parsing structured responses.
// ─────────────────────────────────────────────────────────────────────────────

// Dataset keyword mapping for smart query routing
const DATASET_KEYWORDS = {
  schedule:     ["schedule","activity","activities","float","critical path","start date","finish date","duration","milestone","delay","lookahead","percent complete"],
  rfis:         ["rfi","rfis","request for information","clarification","unanswered"],
  submittals:   ["submittal","submittals","shop drawing","approval","under review","resubmit"],
  quantities:   ["quantity","quantities","volume","cubic yard","takeoff","concrete amount","variance","yardage","pour volume","pour element"],
  changeOrders: ["change order","change orders","pco","pending approval","approved amount","cost exposure"],
  contracts:    ["contract value","prime contract","executed contract","contract amount","scope of work"],
};

const CROSS_REFERENCE_TRIGGERS = [
  "what needs attention","daily briefing","briefing","total exposure","cost exposure",
  "all open items","everything","across all","project health","what is blocking",
];

function classifyQuery(question, availableDatasets) {
  const q = question.toLowerCase();
  if (CROSS_REFERENCE_TRIGGERS.some(t => q.includes(t))) return Object.keys(availableDatasets);

  const matched = new Set();
  for (const [key, keywords] of Object.entries(DATASET_KEYWORDS)) {
    if (!availableDatasets[key]) continue;
    if (keywords.some(kw => q.includes(kw))) matched.add(key);
  }

  if (matched.size === 0) return Object.keys(availableDatasets);
  if (matched.has("rfis") && availableDatasets.changeOrders) matched.add("changeOrders");
  return Array.from(matched);
}

// Parse Claude's JSON response with fallbacks
export function parseResponse(raw) {
  let s = (raw || "").replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
  try { const p = JSON.parse(s); if (p && p.answer !== undefined) return p; } catch {}
  const st = s.indexOf("{"), en = s.lastIndexOf("}");
  if (st !== -1 && en > st) {
    try { const p = JSON.parse(s.slice(st, en + 1)); if (p && p.answer !== undefined) return p; } catch {}
  }
  return { answer: s, insight: "", followUpSuggestions: [] };
}

// Main chat function
export async function sendChatMessage({ question, datasets, mode, modeInstruction, companyName, projectName, projectNumber }) {
  // Classify query to send minimal datasets
  const neededKeys = classifyQuery(question, datasets);
  const filteredDatasets = Object.fromEntries(
    neededKeys
      .filter(k => datasets[k])
      .map(k => [k, { label: datasets[k].label, rows: datasets[k].rows }])
  );

  // Safety: if filtering left nothing, send all
  const datasetsToSend = Object.keys(filteredDatasets).length > 0
    ? filteredDatasets
    : Object.fromEntries(Object.entries(datasets).map(([k, v]) => [k, { label: v.label, rows: v.rows }]));

  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question,
      modeInstruction: modeInstruction || "",
      dataContext: {
        companyName: companyName || "CHAD Construction",
        projectName: projectName || "All Projects",
        projectNumber: projectNumber || "PORTFOLIO",
        datasets: datasetsToSend,
      },
    }),
  });

  if (!res.ok) throw new Error(`API error: ${res.status}`);

  const { result } = await res.json();
  return parseResponse(result);
}
